import {legalPartyNamesLikelyMatch} from './legalReviewPositionMatch.js';

/** 合同当事方国有属性判定口径。 */
export type PartyOwnershipKind = 'state-wholly-owned' | 'state-holding' | 'state-controlled' | 'non-state' | 'unknown';

/** 国资监管归属，按出资人类型划分，不按具体企业名称白名单。 */
export type StateOwnerKind = 'sasac-central' | 'state-council' | 'mof-finance' | 'local-sasac' | 'institution';

/** 识别到的国资出资人或监管主体。 */
export type StateOwnerHit = {
  kind: StateOwnerKind;
  tag: string;
  source: string;
};

/** 合同预览中的当事方及国有属性标注。 */
export type LegalReviewParty = {
  name: string;
  role: string;
  creditCode?: string;
  ownershipKind?: PartyOwnershipKind;
  ownershipLabel?: string;
  ownershipTag?: string;
  ownershipBasis?: string;
  econKind?: string;
  actualController?: string;
  shareholders?: PartyShareholder[];
  relatedNames?: string[];
};

/** 股东持股快照。 */
export type PartyShareholder = {
  name: string;
  ratio?: number;
};

/** 工商详情与股权线索，供规则判定。 */
export type PartyOwnershipEvidence = {
  name: string;
  creditCode?: string;
  econKind?: string;
  actualController?: string;
  shareholders?: PartyShareholder[];
  /** 沿股权向上查到的国资监管主体。 */
  ultimateOwner?: StateOwnerHit;
  /** 本公司及向上穿透过程中出现的企业名称。 */
  relatedNames?: string[];
  /** 企查查司库企业属性：1中央企业 2地方国有企业 3中央部委 4地方政府 5民营企业 7事业单位 8个体工商户。 */
  qccCompanyType?: string;
  parent?: Pick<LegalReviewParty, 'name' | 'ownershipKind' | 'ownershipLabel' | 'ownershipBasis' | 'actualController' | 'ownershipTag'>;
};

/**
 * 把企查查司库企业属性转成国有判定，便于 2003 一次查询即可分类。
 * @param value CompanyType 字段
 */
export function mapQccCompanyType(value: unknown): {kind: PartyOwnershipKind; label: string} | null {
  const raw = String(value ?? '').trim();
  if (raw === '1' || raw === '3') return {kind: 'state-holding', label: raw === '3' ? '中央部委' : '中央企业'};
  if (raw === '2' || raw === '4') return {kind: 'state-holding', label: raw === '4' ? '地方政府' : '地方国有企业'};
  if (raw === '5' || raw === '8') return {kind: 'non-state', label: raw === '8' ? '个体工商户' : '民营企业'};
  if (raw === '7') return {kind: 'state-controlled', label: '事业单位'};
  return null;
}

const ORG_NAME = /公司|企业|集团|合伙|合作社|事务所|医院|大学|学院|委员会|中心|银行|保险|基金|厂|局|厅|部$/u;
const PRIVATE_ECON = /私营|外商投资|港澳台|自然人独资|自然人投资或控股/u;
const OWNER_RANK: Record<StateOwnerKind, number> = {
  'sasac-central': 5,
  'state-council': 4,
  'mof-finance': 3,
  'local-sasac': 2,
  institution: 1,
};

/**
 * 把国资监管主体名称写成一行简述，如「北京市西城区国资委所属企业」。
 * @param source 实际控制人或出资人全称
 * @param kind 国资监管类别
 */
export function formatStateOwnerAffiliation(source: string, kind?: StateOwnerKind): string {
  const raw = String(source ?? '').trim();
  if (kind === 'sasac-central' || /国务院国有资产监督管理委员会|国务院国资委/u.test(raw)) {
    return '国务院国资委所属企业';
  }
  if (kind === 'mof-finance' || /中华人民共和国财政部|^财政部$/u.test(raw)) {
    return '财政部所属企业';
  }
  if (kind === 'state-council' || /中华人民共和国国务院|^国务院$|^国务院办公厅$/u.test(raw)) {
    return '国务院所属企业';
  }
  if (kind === 'institution' || /事业单位/u.test(raw)) {
    const short = raw.replace(/[（(]?事业单位[)）]?/g, '').trim();
    return short && short.length <= 20 ? `${short}所属企业` : '事业单位所属企业';
  }
  const compact = raw.replace(/人民政府/g, '').replace(/国有资产监督管理委员会/g, '国资委').replace(/\s+/g, '');
  if (compact) return compact.endsWith('所属企业') ? compact : `${compact}所属企业`;
  return '地方国资委所属企业';
}

/**
 * 预览里跟在当事方名称后的一行简述。
 * @param party 已判定的当事方
 */
export function formatPartyOwnershipBrief(party: {
  ownershipKind?: string;
  ownershipLabel?: string;
  ownershipTag?: string;
  ownershipBasis?: string;
  actualController?: string;
  econKind?: string;
}): string {
  const tag = String(party.ownershipTag ?? '').trim();
  if (/所属企业$/.test(tag)) return tag;
  if (party.ownershipLabel === '自然人') return '自然人';
  if (party.ownershipKind === 'non-state' || party.ownershipLabel === '非国有') return '非国有企业';
  const basis = String(party.ownershipBasis ?? '');
  const sourced = basis.match(/（([^）]+)）/u)?.[1]
    || String(party.actualController ?? '').trim();
  if (sourced && /国资委|财政部|国务院|事业单位|财政厅|财政局|人民政府/u.test(sourced)) {
    return formatStateOwnerAffiliation(sourced);
  }
  if (tag === '国资委央企') return '国务院国资委所属企业';
  if (tag === '国务院管理央企') return '国务院所属企业';
  if (tag === '财政部金融央企') return '财政部所属企业';
  if (tag === '事业单位所属') return '事业单位所属企业';
  if (tag === '地方国企' && sourced) return formatStateOwnerAffiliation(sourced);
  if (/属国企$/.test(tag) && sourced) return formatStateOwnerAffiliation(sourced);
  if (party.ownershipKind?.startsWith('state-')) return sourced ? formatStateOwnerAffiliation(sourced) : '国有企业';
  const econKind = String(party.econKind ?? '').trim();
  if (econKind) {
    if (/国有独资|全民所有制|国有控股/u.test(econKind)) return econKind.length > 36 ? `${econKind.slice(0, 36)}…` : econKind;
    if (/法人独资/u.test(econKind)) return '法人独资（已查工商，待股东穿透）';
    return econKind.length > 36 ? `工商：${econKind.slice(0, 34)}…` : `工商：${econKind}`;
  }
  const econFromBasis = basis.match(/企业类型：([^；。]+)/u)?.[1]?.trim();
  if (econFromBasis) {
    return econFromBasis.length > 36 ? `${econFromBasis.slice(0, 36)}…` : econFromBasis;
  }
  if (party.ownershipKind === 'unknown' && party.actualController) {
    const controller = String(party.actualController).trim();
    return controller.length > 24 ? `实控：${controller.slice(0, 22)}…` : `实控：${controller}`;
  }
  return '';
}

/**
 * 识别名称或企业类型是否指向国资监管主体、财政出资人或事业单位。
 * 只认出资人类型（国务院、国资委、财政部、地方国资委、事业单位），不按企业名称白名单。
 * @param name 股东、实际控制人或本企业名称
 * @param econKind 工商企业类型
 */
export function recognizeStateOwner(name: string, econKind = ''): StateOwnerHit | null {
  const trimmed = String(name ?? '').trim();
  const kindText = String(econKind ?? '').trim();
  if (/国务院国有资产监督管理委员会|国务院国资委/u.test(trimmed)) {
    return {kind: 'sasac-central', tag: formatStateOwnerAffiliation(trimmed, 'sasac-central'), source: trimmed};
  }
  if (/中华人民共和国财政部|^财政部$/u.test(trimmed)) {
    return {kind: 'mof-finance', tag: formatStateOwnerAffiliation(trimmed, 'mof-finance'), source: trimmed};
  }
  if (/中华人民共和国国务院|^国务院$|^国务院办公厅$/u.test(trimmed)) {
    return {kind: 'state-council', tag: formatStateOwnerAffiliation(trimmed, 'state-council'), source: trimmed};
  }
  if (/国有资产监督管理委员会|国资委|财政厅|财政局|人民政府/u.test(trimmed) && !/国务院/u.test(trimmed)) {
    return {kind: 'local-sasac', tag: formatStateOwnerAffiliation(trimmed, 'local-sasac'), source: trimmed};
  }
  if (/事业单位/u.test(trimmed) || /事业/u.test(kindText)) {
    return {kind: 'institution', tag: formatStateOwnerAffiliation(trimmed || kindText, 'institution'), source: trimmed || kindText};
  }
  return null;
}

/**
 * 名称是否指向国资监管主体、财政出资人或事业单位。
 * @param name 股东或实际控制人名称
 * @param econKind 可选企业类型
 */
export function isStateRelatedPartyName(name: string, econKind = ''): boolean {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return false;
  if (recognizeStateOwner(trimmed, econKind)) return true;
  return /国有|国营|国有资本/u.test(trimmed);
}

/**
 * 是否已判定为国家出资企业（含国有独资、控股、实际控制）。
 * @param kind 国有属性
 */
export function isStateOwnedPartyKind(kind?: string | null): boolean {
  return kind === 'state-wholly-owned' || kind === 'state-holding' || kind === 'state-controlled';
}

/** 从国资监管主体名称解析出的中央/地方层级，供审核选用规范。 */
export type StateOwnerLocality = {
  level: 'central' | 'local';
  regulator: string;
  locality: string;
  layers: string[];
};

const DIRECT_MUNICIPALITIES = ['北京市', '上海市', '天津市', '重庆市'] as const;

/**
 * 把「北京市西城区」拆成「北京市」「西城区」，便于同时对照市、区国资规定。
 * @param locality 监管主体名称去掉国资委后缀后的行政区划
 */
export function splitStateOwnerLocalityLayers(locality: string): string[] {
  const text = String(locality ?? '').trim();
  if (!text) return [];
  for (const city of DIRECT_MUNICIPALITIES) {
    if (text.startsWith(city)) {
      const rest = text.slice(city.length).trim();
      return rest ? [city, rest] : [city];
    }
  }
  const matched = text.match(/^(.+?(?:省|自治区|特别行政区))(.+?市)?(.*)$/u);
  if (matched) {
    return [matched[1], matched[2], matched[3]].map(item => String(item ?? '').trim()).filter(Boolean);
  }
  return [text];
}

/**
 * 从出资人或监管主体全称解析中央/省/市/区层级。
 * @param source 实际控制人或国资委全称
 * @param kind 已识别的出资人类型
 */
export function parseStateOwnerLocality(source: string, kind?: StateOwnerKind): StateOwnerLocality | null {
  const raw = String(source ?? '').trim();
  const hit = raw ? recognizeStateOwner(raw) : null;
  const ownerKind = hit?.kind ?? kind;
  if (!raw && !ownerKind) return null;
  if (ownerKind === 'sasac-central' || ownerKind === 'state-council' || ownerKind === 'mof-finance') {
    const regulator = ownerKind === 'mof-finance'
      ? '财政部'
      : ownerKind === 'state-council'
        ? '国务院'
        : '国务院国资委';
    return {level: 'central', regulator, locality: '中央', layers: ['中央']};
  }
  const fromHit = String(hit?.source || raw).trim();
  if (!fromHit) return null;
  const compact = fromHit.replace(/人民政府/g, '').replace(/国有资产监督管理委员会/g, '国资委').replace(/\s+/g, '');
  const regulator = compact || '地方国资委';
  const locality = regulator.replace(/国资委|财政厅|财政局$/u, '').replace(/所属企业$/u, '') || regulator;
  return {
    level: 'local',
    regulator,
    locality,
    layers: splitStateOwnerLocalityLayers(locality),
  };
}

/**
 * 名称是否更像自然人，应跳过工商国企识别。
 * @param name 当事方名称
 */
export function isLikelyNaturalPersonParty(name: string): boolean {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return true;
  if (ORG_NAME.test(trimmed)) return false;
  if (/[A-Za-z0-9]/.test(trimmed)) return false;
  return /^[\u4e00-\u9fff·]{2,4}$/u.test(trimmed);
}

/**
 * 解析持股比例文本。
 * @param value 如 51%、51.2
 */
export function parseShareRatio(value: unknown): number | undefined {
  const text = String(value ?? '').replace(/,/g, '').trim();
  if (!text) return undefined;
  const matched = text.match(/(\d+(?:\.\d+)?)\s*%?/);
  if (!matched) return undefined;
  const ratio = Number(matched[1]);
  return Number.isFinite(ratio) ? ratio : undefined;
}

function ownershipLabel(kind: PartyOwnershipKind): string {
  if (kind === 'state-wholly-owned') return '国有独资';
  if (kind === 'state-holding') return '国有控股';
  if (kind === 'state-controlled') return '国有实际控制';
  if (kind === 'non-state') return '非国有';
  return '未能判定';
}

/**
 * 从本层工商线索中取出优先级最高的国资出资人。
 * @param evidence 工商与股权线索
 */
export function bestStateOwnerOnEvidence(evidence: PartyOwnershipEvidence): StateOwnerHit | null {
  const hits: StateOwnerHit[] = [];
  const push = (hit: StateOwnerHit | null): void => {
    if (hit) hits.push(hit);
  };
  push(recognizeStateOwner(String(evidence.actualController ?? '')));
  push(recognizeStateOwner(String(evidence.name ?? ''), String(evidence.econKind ?? '')));
  for (const item of evidence.shareholders ?? []) {
    push(recognizeStateOwner(item.name));
  }
  if (evidence.ultimateOwner) hits.push(evidence.ultimateOwner);
  if (evidence.parent?.actualController) push(recognizeStateOwner(evidence.parent.actualController));
  if (evidence.parent?.ownershipTag && evidence.parent.actualController) {
    push(recognizeStateOwner(evidence.parent.actualController));
  }
  return hits.sort((left, right) => OWNER_RANK[right.kind] - OWNER_RANK[left.kind])[0] ?? null;
}

/**
 * 该国资主体是否已是监管终端（无需再向上查企业股东）。
 * @param hit 国资识别结果
 */
export function isTerminalStateOwner(hit: StateOwnerHit | null): boolean {
  return Boolean(hit && hit.kind !== 'institution');
}

/**
 * 保留国资归属更具体的一条（国务院国资委优先于事业单位）。
 * @param current 已有识别
 * @param next 新识别
 */
export function preferStateOwner(current: StateOwnerHit | null | undefined, next: StateOwnerHit | null | undefined): StateOwnerHit | undefined {
  if (!next) return current ?? undefined;
  if (!current) return next;
  return OWNER_RANK[next.kind] > OWNER_RANK[current.kind] ? next : current;
}

function stateShareTotal(shareholders: PartyShareholder[]): number {
  return shareholders
    .filter(item => isStateRelatedPartyName(item.name))
    .reduce((sum, item) => sum + (item.ratio ?? 0), 0);
}

/**
 * 根据企业类型、股东、实际控制人和向上查到的国资监管主体判定国有属性。
 * @param evidence 工商与股权线索
 */
export function classifyPartyStateOwnership(evidence: PartyOwnershipEvidence): LegalReviewParty {
  const name = String(evidence.name ?? '').trim();
  const econKind = String(evidence.econKind ?? '').trim();
  const actualController = String(evidence.actualController ?? '').trim();
  const shareholders = evidence.shareholders ?? [];
  const basis: string[] = [];
  let kind: PartyOwnershipKind = 'unknown';
  const owner = bestStateOwnerOnEvidence(evidence);

  if (/全民所有制|国有独资/u.test(econKind)) {
    kind = 'state-wholly-owned';
    basis.push(`企业类型：${econKind}`);
  } else if (/国有控股/u.test(econKind) || (/国有/u.test(econKind) && !/法人独资/u.test(econKind))) {
    kind = 'state-holding';
    basis.push(`企业类型：${econKind}`);
  }

  const stateRatio = stateShareTotal(shareholders);
  if (kind === 'unknown' && stateRatio >= 99) {
    kind = 'state-wholly-owned';
    basis.push(`国有股东合计持股约 ${stateRatio.toFixed(2)}%`);
  } else if (kind === 'unknown' && stateRatio >= 50) {
    kind = 'state-holding';
    basis.push(`国有股东合计持股约 ${stateRatio.toFixed(2)}%`);
  }
  if (actualController && isStateRelatedPartyName(actualController)) {
    if (kind === 'unknown') kind = stateRatio > 0 && stateRatio < 50 ? 'state-controlled' : 'state-holding';
    basis.push(`实际控制人：${actualController}`);
  }
  const parent = evidence.parent;
  if (kind === 'unknown' && parent?.ownershipKind && parent.ownershipKind.startsWith('state-')) {
    kind = 'state-holding';
    basis.push(`股东「${parent.name}」为${parent.ownershipLabel}${parent.actualController ? `，其实际控制人：${parent.actualController}` : ''}`);
  }
  if (kind === 'unknown' && owner) {
    kind = /法人独资|国有独资|全民所有制/u.test(econKind) || stateRatio >= 99 ? 'state-wholly-owned' : 'state-holding';
    basis.push(`国资归属：${formatStateOwnerAffiliation(owner.source, owner.kind)}（${owner.source}）`);
  }
  const allNaturalShareholders = shareholders.length > 0
    && shareholders.every(item => isLikelyNaturalPersonParty(item.name));
  if (kind === 'unknown' && PRIVATE_ECON.test(econKind)) {
    kind = 'non-state';
    basis.push(`企业类型：${econKind}`);
  } else if (kind === 'unknown' && allNaturalShareholders && !isStateRelatedPartyName(actualController)) {
    kind = 'non-state';
    if (econKind) basis.push(`企业类型：${econKind}`);
    if (actualController) basis.push(`实际控制人：${actualController}`);
  } else if (kind === 'unknown' && econKind) {
    basis.push(`企业类型：${econKind}${/法人独资/.test(econKind) ? '。法人独资只说明唯一股东是企业，需沿股权找到国资委、财政部、国务院或事业单位后再认定' : ''}`);
  }
  const mappedType = kind === 'unknown' ? mapQccCompanyType(evidence.qccCompanyType) : null;
  if (mappedType) {
    kind = mappedType.kind;
    basis.push(`企查查企业属性：${mappedType.label}`);
  }

  const tag = kind === 'non-state'
    ? '非国有企业'
    : kind === 'unknown'
      ? ''
      : (owner ? formatStateOwnerAffiliation(owner.source, owner.kind) : '国有企业');
  return {
    name,
    role: '',
    creditCode: evidence.creditCode,
    ownershipKind: kind,
    ownershipLabel: ownershipLabel(kind),
    ownershipTag: tag || undefined,
    ownershipBasis: basis.join('；') || undefined,
    econKind: econKind || undefined,
    actualController: actualController || undefined,
  };
}

/** 预览时用于判断当事方之间有无股权或控制关联。 */
export type PartyRelationSnapshot = {
  name: string;
  role?: string;
  ownershipKind?: string;
  actualController?: string;
  shareholders?: PartyShareholder[];
  relatedNames?: string[];
};

/**
 * 出资人是否为国资监管主体，不能当作企业之间的关联方。
 * @param name 股东或实际控制人
 */
function isStateRegulatorName(name: string): boolean {
  const hit = recognizeStateOwner(name);
  return Boolean(hit && hit.kind !== 'institution');
}

function partyRoleLabel(party: PartyRelationSnapshot): string {
  const role = String(party.role ?? '').trim();
  const short = role.match(/^(甲方|乙方|丙方|丁方|戊方|己方|发包人|承包人|监理人|担保人|被担保人|出租人|承租人)/u);
  if (short) return short[1];
  return role || String(party.name ?? '').trim();
}

function namesMatchIn(list: string[] | undefined, target: string): boolean {
  return (list ?? []).some(item => legalPartyNamesLikelyMatch(item, target));
}

function firstSharedCorporateName(left: string[], right: string[], exclude: string[]): string {
  for (const name of left) {
    if (!name || isStateRegulatorName(name) || exclude.some(item => legalPartyNamesLikelyMatch(item, name))) continue;
    if (right.some(item => legalPartyNamesLikelyMatch(item, name))) return name;
  }
  return '';
}

function joinPartyLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]}、${labels[1]}`;
  return `${labels.slice(0, -1).join('、')}和${labels[labels.length - 1]}`;
}

/**
 * 用已查询的股东、实际控制人和股权链，概括当事方之间有无关联。
 * @param parties 已核验工商的当事方
 */
export function summarizePartyRelations(parties: PartyRelationSnapshot[]): string {
  const items = parties.filter(item => String(item.name ?? '').trim());
  if (items.length < 2) return '';
  const known = items.some(item => item.ownershipKind && item.ownershipKind !== 'unknown'
    || String(item.actualController ?? '').trim()
    || (item.shareholders ?? []).length
    || (item.relatedNames ?? []).length);
  if (!known) return '';

  const parentOf = new Map<number, {parent: number; direct: boolean}>();
  const linked = new Set<string>();
  const pairKey = (i: number, j: number): string => [i, j].sort((a, b) => a - b).join('-');
  const markParent = (parent: number, child: number, direct: boolean): void => {
    if (parent === child || parentOf.get(child)?.direct) return;
    const current = parentOf.get(child);
    if (current && !direct) return;
    parentOf.set(child, {parent, direct});
    linked.add(pairKey(parent, child));
  };

  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      const left = items[i];
      const right = items[j];
      if (legalPartyNamesLikelyMatch(left.name, right.name)) continue;
      const leftShareholderIsRight = (left.shareholders ?? []).some(item => legalPartyNamesLikelyMatch(item.name, right.name));
      const rightShareholderIsLeft = (right.shareholders ?? []).some(item => legalPartyNamesLikelyMatch(item.name, left.name));
      if (leftShareholderIsRight) markParent(j, i, true);
      else if (rightShareholderIsLeft) markParent(i, j, true);
      else if (namesMatchIn(left.relatedNames, right.name)) markParent(j, i, false);
      else if (namesMatchIn(right.relatedNames, left.name)) markParent(i, j, false);
    }
  }

  const sentences: string[] = [];
  const childrenByParent = new Map<number, {child: number; direct: boolean}[]>();
  for (const [child, link] of parentOf.entries()) {
    const list = childrenByParent.get(link.parent) ?? [];
    list.push({child, direct: link.direct});
    childrenByParent.set(link.parent, list);
  }
  for (const [parent, children] of childrenByParent.entries()) {
    const labels = children.map(item => partyRoleLabel(items[item.child]));
    const parentLabel = partyRoleLabel(items[parent]);
    const allDirect = children.every(item => item.direct);
    sentences.push(allDirect
      ? `${parentLabel}是${joinPartyLabels(labels)}的股东`
      : `${joinPartyLabels(labels)}为${parentLabel}下属企业`);
  }

  const extra: string[] = [];
  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      if (linked.has(pairKey(i, j))) continue;
      if (parentOf.get(i)?.parent === parentOf.get(j)?.parent && parentOf.has(i) && parentOf.has(j)) continue;
      const left = items[i];
      const right = items[j];
      if (legalPartyNamesLikelyMatch(left.name, right.name)) continue;
      const leftCtrl = String(left.actualController ?? '').trim();
      const rightCtrl = String(right.actualController ?? '').trim();
      if (leftCtrl && rightCtrl && legalPartyNamesLikelyMatch(leftCtrl, rightCtrl) && !isStateRegulatorName(leftCtrl)) {
        extra.push(`${partyRoleLabel(left)}与${partyRoleLabel(right)}同受「${leftCtrl}」实际控制`);
        linked.add(pairKey(i, j));
        continue;
      }
      const shared = firstSharedCorporateName(
        left.relatedNames ?? [],
        right.relatedNames ?? [],
        [left.name, right.name, ...items.map(item => item.name)],
      );
      if (shared) {
        extra.push(`${partyRoleLabel(left)}与${partyRoleLabel(right)}同属「${shared}」`);
        linked.add(pairKey(i, j));
      }
    }
  }
  sentences.push(...extra);

  if (sentences.length) return `${sentences.slice(0, 3).join('；')}。`;
  const count = items.length === 2 ? '两家当事方' : items.length === 3 ? '三家当事方' : `${items.length}家当事方`;
  return `${count}未发现相互持股或同一企业实际控制人。`;
}
